import type { Student, SeatingChart, Table, Seat, FocalEdge } from "../../types/models";
import { buildDistanceMatrix, type DistanceMatrix } from "./distance";
import { getApartPairs } from "./constraints";
import { shuffle } from "@logic/random";

/**
 * Get the absolute position of a seat in the chart
 */
function getSeatAbsolutePosition(seat: Seat, table: Table): { x: number; y: number } {
    // Default table size if not specified
    const tableWidth = table.width ?? 100;
    const tableHeight = table.height ?? 100;
    const cx = tableWidth / 2;
    const cy = tableHeight / 2;

    // Seat position relative to table center
    const seatX = seat.x ?? 0;
    const seatY = seat.y ?? 0;

    return {
        x: table.x + cx + seatX,
        y: table.y + cy + seatY
    };
}

/**
 * Calculate distance from a seat to the focal edge
 * Lower values = closer to the edge (higher priority)
 */
function getDistanceToFocalEdge(
    seat: Seat,
    table: Table,
    focalEdge: FocalEdge,
    chartBounds: { maxX: number; maxY: number }
): number {
    if (focalEdge === 'none') return 0;

    const pos = getSeatAbsolutePosition(seat, table);

    switch (focalEdge) {
        case 'top':
            return pos.y; // Lower y = closer to top
        case 'bottom':
            return chartBounds.maxY - pos.y; // Higher y = closer to bottom
        case 'left':
            return pos.x; // Lower x = closer to left
        case 'right':
            return chartBounds.maxX - pos.x; // Higher x = closer to right
        default:
            return 0;
    }
}

/**
 * Get chart bounds (max x and y of all tables)
 */
function getChartBounds(chart: SeatingChart): { maxX: number; maxY: number } {
    let maxX = 0;
    let maxY = 0;

    for (const table of chart.tables) {
        const tableRight = table.x + (table.width ?? 100);
        const tableBottom = table.y + (table.height ?? 100);
        if (tableRight > maxX) maxX = tableRight;
        if (tableBottom > maxY) maxY = tableBottom;
    }

    return { maxX, maxY };
}

/**
 * Result of a placement attempt
 */
export interface PlacementResult {
    seatAssignments: Map<string, string | undefined>;
    unplacedStudents: string[];
}

/**
 * Find all empty seats in a chart
 */
export function getEmptySeats(
    chart: SeatingChart,
    assignments: Map<string, string | undefined>
): Seat[] {
    const emptySeats: Seat[] = [];

    for (const table of chart.tables) {
        for (const seat of table.seats) {
            if (!assignments.get(seat.id)) {
                emptySeats.push(seat);
            }
        }
    }

    return emptySeats;
}

/**
 * Check if a student can be placed in a seat respecting "apart" constraints
 */
export function canPlaceStudent(
    studentId: string,
    seatId: string,
    assignments: Map<string, string | undefined>,
    apartPairs: [string, string, number][],
    matrix: DistanceMatrix
): boolean {
    // Check all "apart" constraints involving this student
    for (const [idA, idB, minDistance] of apartPairs) {
        let otherStudentId: string | null = null;

        if (idA === studentId) {
            otherStudentId = idB;
        } else if (idB === studentId) {
            otherStudentId = idA;
        }

        if (otherStudentId) {
            // Find where the other student is seated
            for (const [otherSeatId, assignedId] of assignments) {
                if (assignedId === otherStudentId) {
                    const distance = matrix.get(seatId, otherSeatId);
                    if (distance < minDistance && distance !== Infinity) {
                        return false;
                    }
                }
            }
        }
    }

    return true;
}

/**
 * Find the best seat for a student considering constraints and focal edge
 */
export function findBestSeat(
    studentId: string,
    emptySeats: Seat[],
    assignments: Map<string, string | undefined>,
    apartPairs: [string, string, number][],
    matrix: DistanceMatrix,
    chart?: SeatingChart
): Seat | null {
    // Shuffle to add randomness first
    let sortedSeats = shuffle([...emptySeats]);

    // If focal edge is set, sort by distance to edge (closer first)
    if (chart?.focalEdge && chart.focalEdge !== 'none') {
        const bounds = getChartBounds(chart);
        const tableMap = new Map<string, Table>();
        for (const table of chart.tables) {
            for (const seat of table.seats) {
                tableMap.set(seat.id, table);
            }
        }

        sortedSeats.sort((a, b) => {
            const tableA = tableMap.get(a.id);
            const tableB = tableMap.get(b.id);
            if (!tableA || !tableB) return 0;

            const distA = getDistanceToFocalEdge(a, tableA, chart.focalEdge!, bounds);
            const distB = getDistanceToFocalEdge(b, tableB, chart.focalEdge!, bounds);
            return distA - distB;
        });
    }

    for (const seat of sortedSeats) {
        if (canPlaceStudent(studentId, seat.id, assignments, apartPairs, matrix)) {
            return seat;
        }
    }

    // If no seat satisfies constraints, return first available (will record violation)
    return sortedSeats[0] ?? null;
}

/**
 * Place a group of students who must sit together ("within" constraint)
 * Tries to find adjacent seats on the same table
 */
export function placeGroupTogether(
    studentIds: string[],
    chart: SeatingChart,
    assignments: Map<string, string | undefined>,
    matrix: DistanceMatrix,
    maxRadius: number
): boolean {
    // Find tables with enough empty adjacent seats
    for (const table of shuffle([...chart.tables])) {
        const emptySeatsOnTable = table.seats.filter(s => !assignments.get(s.id));

        if (emptySeatsOnTable.length < studentIds.length) continue;

        // Try to find a contiguous block of seats
        const seatPositions = emptySeatsOnTable.map(s => s.position).sort((a, b) => a - b);

        // Try each starting position
        for (const startSeat of shuffle([...emptySeatsOnTable])) {
            const group: Seat[] = [startSeat];

            // Find adjacent seats
            for (const seat of emptySeatsOnTable) {
                if (seat.id === startSeat.id) continue;
                if (group.length >= studentIds.length) break;

                // Check if this seat is within radius of all seats in group
                const withinRadius = group.every(g => {
                    const dist = matrix.get(g.id, seat.id);
                    return dist <= maxRadius;
                });

                if (withinRadius) {
                    group.push(seat);
                }
            }

            if (group.length >= studentIds.length) {
                // Place students in these seats
                const shuffledStudents = shuffle([...studentIds]);
                for (let i = 0; i < shuffledStudents.length; i++) {
                    const seat = group[i];
                    const student = shuffledStudents[i];
                    if (seat && student) {
                        assignments.set(seat.id, student);
                    }
                }
                return true;
            }
        }
    }

    return false;
}

/**
 * Greedy placement of students into seats
 * Places students one by one, respecting constraints
 */
export function greedyPlacement(
    students: Student[],
    chart: SeatingChart,
    withinGroups: string[][],
    existingAssignments?: Map<string, string | undefined>
): PlacementResult {
    const assignments = existingAssignments
        ? new Map(existingAssignments)
        : new Map<string, string | undefined>();

    // Initialize all seats as empty if starting fresh
    if (!existingAssignments) {
        for (const table of chart.tables) {
            for (const seat of table.seats) {
                assignments.set(seat.id, undefined);
            }
        }
    }

    const matrix = buildDistanceMatrix(chart);
    const apartPairs = getApartPairs(chart);

    // Track which students are already placed
    const placedStudents = new Set<string>();
    for (const studentId of assignments.values()) {
        if (studentId) placedStudents.add(studentId);
    }

    // Get present students who need placement
    const presentStudents = students.filter(s => !s.absent && !placedStudents.has(s.id));
    const unplacedStudents: string[] = [];

    // Step 1: Place "within" groups first
    for (const group of withinGroups) {
        const groupStudents = group.filter(id =>
            presentStudents.some(s => s.id === id) && !placedStudents.has(id)
        );

        if (groupStudents.length === 0) continue;

        // Find the max radius for this group from constraints
        const relevantConstraint = chart.proximityConstraints.find(c =>
            c.type === 'within' &&
            groupStudents.every(id => c.studentIds.includes(id))
        );
        const maxRadius = relevantConstraint?.radius ?? 1;

        const placed = placeGroupTogether(groupStudents, chart, assignments, matrix, maxRadius);

        if (placed) {
            groupStudents.forEach(id => placedStudents.add(id));
        } else {
            // Could not place together, will place individually
        }
    }

    // Step 2: Place remaining students individually
    const remainingStudents = shuffle(
        presentStudents.filter(s => !placedStudents.has(s.id))
    );

    for (const student of remainingStudents) {
        const emptySeats = getEmptySeats(chart, assignments);

        if (emptySeats.length === 0) {
            unplacedStudents.push(student.id);
            continue;
        }

        const seat = findBestSeat(student.id, emptySeats, assignments, apartPairs, matrix, chart);

        if (seat) {
            assignments.set(seat.id, student.id);
            placedStudents.add(student.id);
        } else {
            unplacedStudents.push(student.id);
        }
    }

    return { seatAssignments: assignments, unplacedStudents };
}
